/**
 * NOTIF-003 — the notification endpoints (NOTIF-001 / NOTIF-002 on the API).
 * Thin, typed wrappers; every call is scoped to the signed-in user by the server.
 */
import apiService, { resolveApiBaseUrl } from '@/services/api'
import { ExportDownloadError, absoluteExportUrl } from '@/lib/notifications/exports'

export type NotificationLevel = 'action_needed' | 'heads_up' | 'info'

export interface NotificationItem {
  id: string
  type: string
  module: string
  page_key: string | null
  level: NotificationLevel
  title: string
  body: string | null
  url: string | null
  data: Record<string, unknown>
  read_at: string | null
  created_at: string
  /** NOTIF-011 — when the row last changed state (an export becoming ready); null until then. */
  touched_at?: string | null
}

export interface UnreadSummary {
  total: number
  by_page_key: Record<string, number>
  by_level: Partial<Record<NotificationLevel, number>>
  latest_at: string | null
  /** NOTIF-007b — the server's clock when the summary was built: the announce mark when nothing is unread. */
  as_of?: string
  /** NOTIF-012 — how to open the real-time link, when the switch is on (absent on an older API). */
  realtime?: RealtimeConfig
}

/**
 * NOTIF-012 — the server's word on real-time delivery (NotificationsPush::clientConfig): off, or
 * the public WebSocket address, the app key and the person's own channel (`users.{uuid}`).
 */
export interface RealtimeConfig {
  enabled: boolean
  key?: string
  host?: string
  port?: number
  scheme?: 'http' | 'https'
  channel?: string
}

export interface NotificationPageMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

/** NOTIF-008 — is the record a notification links to still there? NOTIF-011: `expired` — an export's download window closed. */
export type NotificationTargetState = 'ok' | 'deleted' | 'missing' | 'expired'

export interface NotificationTarget {
  state: NotificationTargetState
  /** What the link names; null when there is nothing to check (a plain page). `notification`: the row itself is gone (client-side). */
  entity: 'request' | 'line' | 'submission' | 'export' | 'notification' | null
  /** The record as a person would name it: "Request NEW-1042", "Line NEW-1042-01 of request NEW-1042". */
  label: string | null
  deleted_at: string | null
  deleted_by: string | null
  /**
   * Client-made targets only (never from the API): the server's own sentence when it refused
   * to hand an export's file over — the modal shows it instead of a date from the payload.
   */
  reason?: string | null
  /** The row, as the list would give it (null only for a client-made target). */
  notification: NotificationItem | null
}

/** The three-way choice the toasts and the sound share. */
export type NotificationMode = 'action_needed' | 'all' | 'none'

export interface NotificationSettings {
  toast_mode: NotificationMode
  /** NOTIF-004b: one chime per batch of new rows — for Action needed only, for all, or never. */
  sound_mode: NotificationMode
  browser_enabled: boolean
}

export interface NotificationTypeSetting {
  type: string
  module: string
  module_label: string
  page_key: string | null
  page_label: string | null
  level: NotificationLevel
  title: string
  /** What the settings page calls the type (registry `label`). */
  label: string
  locked: boolean
  /** NOTIF-011 — the channels the person cannot switch off for this type (`locked` = all of them). */
  locked_channels?: string[]
  channels: Record<string, boolean>
}

/** NOTIF-010 — whether Slack knows me (looked up by e-mail) and whether messages can go out at all. */
export type SlackLinkStatus = 'unchecked' | 'linked' | 'not_found' | 'error'

export interface SlackLink {
  status: SlackLinkStatus
  /** @name in Slack, once linked. */
  handle: string | null
  checked_at: string | null
  /** What Slack answered when the status is `error`. */
  error: string | null
  /** Switched on by an administrator and a bot token on the server. */
  available: boolean
  unavailable_reason: 'switched_off' | 'not_configured' | null
}

export interface NotificationSettingsOverview {
  settings: NotificationSettings
  channels: string[]
  toast_modes: NotificationMode[]
  sound_modes: NotificationMode[]
  types: NotificationTypeSetting[]
  slack: SlackLink
}

export interface NotificationSettingsUpdate {
  toast_mode?: NotificationMode
  sound_mode?: NotificationMode
  browser_enabled?: boolean
  preferences?: Array<{ type: string; channel: string; enabled: boolean }>
}

const BASE = '/v1/notifications'

function unwrap<T>(response: { success?: boolean; data?: T } | null | undefined, what: string): T {
  if (!response?.success || response.data === undefined) {
    throw new Error(`Failed to load ${what}`)
  }
  return response.data
}

export async function fetchUnreadSummary(): Promise<UnreadSummary> {
  const response = await apiService.get<{ success?: boolean; data?: UnreadSummary }>(
    `${BASE}/unread-summary`
  )
  return unwrap(response, 'notification summary')
}

/**
 * NOTIF-007 — the notifications page's filters ride on the same list: `levels`, and a day
 * range `from` / `to` as `YYYY-MM-DD` (whole days in the user's timezone, the server decides).
 */
export async function fetchNotifications(
  params: {
    unread?: boolean
    pageKey?: string | null
    levels?: NotificationLevel[]
    from?: string | null
    to?: string | null
    page?: number
    perPage?: number
  } = {}
): Promise<{ items: NotificationItem[]; meta: NotificationPageMeta }> {
  const response = await apiService.get<{
    success?: boolean
    data?: NotificationItem[]
    meta?: NotificationPageMeta
  }>(BASE, {
    params: {
      ...(params.unread ? { unread: 1 } : {}),
      ...(params.pageKey ? { page_key: params.pageKey } : {}),
      ...(params.levels?.length ? { levels: params.levels } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      page: params.page ?? 1,
      per_page: params.perPage ?? 20
    }
  })
  if (!response?.success || !Array.isArray(response.data)) {
    throw new Error('Failed to load notifications')
  }
  return {
    items: response.data,
    meta: response.meta ?? {
      current_page: 1,
      last_page: 1,
      per_page: response.data.length,
      total: response.data.length
    }
  }
}

export async function markNotificationRead(id: string): Promise<NotificationItem> {
  const response = await apiService.patch<{ success?: boolean; data?: NotificationItem }>(
    `${BASE}/${encodeURIComponent(id)}/read`
  )
  return unwrap(response, 'notification')
}

export async function markAllNotificationsRead(
  params: {
    levels?: NotificationLevel[]
    pageKey?: string | null
  } = {}
): Promise<{ updated: number; summary: UnreadSummary }> {
  const response = await apiService.post<{
    success?: boolean
    data?: { updated: number; summary: UnreadSummary }
  }>(`${BASE}/read-all`, {
    ...(params.levels?.length ? { levels: params.levels } : {}),
    ...(params.pageKey ? { page_key: params.pageKey } : {})
  })
  return unwrap(response, 'notification summary')
}

/** NOTIF-007 — the page's checkboxes: these rows (of mine) become read; the new summary comes back. */
export async function markNotificationsRead(
  ids: string[]
): Promise<{ updated: number; summary: UnreadSummary }> {
  const response = await apiService.post<{
    success?: boolean
    data?: { updated: number; summary: UnreadSummary }
  }>(`${BASE}/read-all`, { ids })
  return unwrap(response, 'notification summary')
}

/** NOTIF-007b — "Mark as unread": these rows (of mine) become unread again; the new summary comes back. */
export async function markNotificationsUnread(
  ids: string[]
): Promise<{ updated: number; summary: UnreadSummary }> {
  const response = await apiService.post<{
    success?: boolean
    data?: { updated: number; summary: UnreadSummary }
  }>(`${BASE}/unread`, { ids })
  return unwrap(response, 'notification summary')
}

/** NOTIF-008 — the record's state plus the row itself; 404 when the row is gone or not mine. */
export async function fetchNotificationTarget(id: string): Promise<NotificationTarget> {
  const response = await apiService.get<{ success?: boolean; data?: NotificationTarget }>(
    `${BASE}/${id}/target`
  )
  return unwrap(response, 'notification target')
}

export async function fetchNotificationSettings(): Promise<NotificationSettingsOverview> {
  const response = await apiService.get<{ success?: boolean; data?: NotificationSettingsOverview }>(
    `${BASE}/settings`
  )
  return unwrap(response, 'notification settings')
}

export async function updateNotificationSettings(
  payload: NotificationSettingsUpdate
): Promise<NotificationSettingsOverview> {
  const response = await apiService.put<{ success?: boolean; data?: NotificationSettingsOverview }>(
    `${BASE}/settings`,
    payload
  )
  return unwrap(response, 'notification settings')
}

/** NOTIF-010 — look me up in Slack again, whatever the last outcome was. */
export async function checkSlackLink(): Promise<SlackLink> {
  const response = await apiService.post<{ success?: boolean; data?: { slack: SlackLink } }>(
    `${BASE}/slack/check`
  )
  return unwrap(response, 'Slack link').slack
}

/** NOTIF-010 — a test direct message now; the server says why when it cannot (409 / 422). */
export async function sendSlackTest(): Promise<SlackLink> {
  const response = await apiService.post<{ success?: boolean; data?: { slack: SlackLink } }>(
    `${BASE}/slack/test`
  )
  return unwrap(response, 'Slack link').slack
}

// ---------------------------------------------------------------- NOTIF-011 — exports

/** C2 — one part's signed, short-lived download link (GET v1/exports/{uuid}/download-links). */
export interface ExportDownloadLink {
  part: number
  filename: string
  size_bytes: number | null
  url: string
}

export interface ExportDownloadLinks {
  export_uuid: string
  /** When the links stop working (minutes from now); the SPA asks again at the next click. */
  expires_at: string
  parts: ExportDownloadLink[]
}

/**
 * C2 — the links the browser downloads an export's parts from, minted for this click (they
 * live a few minutes). The server's refusals (409 still being prepared / failed, 410 expired or
 * gone, 404 not mine — let through by the client's validateStatus) come back as an
 * ExportDownloadError carrying the server's words and `errors.status`.
 */
export async function fetchExportDownloadLinks(uuid: string): Promise<ExportDownloadLinks> {
  const response = await apiService.getWithResponse<{
    success?: boolean
    message?: string
    data?: ExportDownloadLinks
    errors?: { status?: string }
  }>(`/v1/exports/${encodeURIComponent(uuid)}/download-links`)
  const body = response.data
  if (response.status >= 400 || !body?.success || !body.data) {
    throw new ExportDownloadError(
      body?.message ? String(body.message) : `Download failed (${response.status})`,
      response.status,
      body?.errors?.status ? String(body.errors.status) : null
    )
  }
  // The server hands each link back as a path (behind the ingress it cannot tell https from
  // http); the origin the SPA reaches its API at goes in front.
  const parts = Array.isArray(body.data.parts) ? body.data.parts : []
  return {
    export_uuid: body.data.export_uuid,
    expires_at: body.data.expires_at,
    parts: parts.map((part) => ({
      ...part,
      url: absoluteExportUrl(String(part.url ?? ''), resolveApiBaseUrl(), window.location.origin)
    }))
  }
}
